import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  FiArrowLeft,
  FiSend,
  FiUser,
  FiChevronUp,
  FiCornerDownRight,
  FiX,
  FiEdit2,
  FiCheck,
  FiTrash2,
  FiAlertTriangle,
} from "react-icons/fi";
import { API_ENDPOINTS } from "../../../constants/constants";

interface ForumComment {
  id: string;
  content: string;
  author: {
    name: string;
    avatar?: string;
  };
  publishedAt: string;
  parentId?: string; // ID du commentaire parent, si c'est une réponse
  isReply?: boolean;
  quotedText?: string; // Texte cité du commentaire parent
  quotedAuthor?: string; // Auteur du texte cité
  isEdited?: boolean;
  isDeleted?: boolean;
  isSolution?: boolean;
  isActive?: boolean;
  likes?: number;
}

interface ForumTopic {
  id: string;
  title: string;
  content: string;
  author: {
    name: string;
    avatar?: string;
  };
  publishedAt: string;
  subscribersCount: number;
  commentsCount: number;
  comments: ForumComment[];
  category?: {
    id: number;
    name: string;
    description?: string;
  };
  isPinned?: boolean;
  isLocked?: boolean;
}

// Add a new interface for API response format
interface PostResponse {
  id: string | number;
  content: string;
  author?: {
    name?: string;
    firstName?: string;
    lastName?: string;
    avatar?: string;
  };
  createdAt?: string;
  updatedAt?: string;
  parentId?: string | number;
  isDeleted?: boolean;
  isSolution?: boolean;
  isActive?: boolean;
  likes?: number;
}

const ForumTopicDetail: React.FC = () => {
  const { topicId } = useParams<{ topicId: string }>();
  const navigate = useNavigate();
  const [topic, setTopic] = useState<ForumTopic | null>(null);
  const [newComment, setNewComment] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [displayedComments, setDisplayedComments] = useState<ForumComment[]>(
    []
  );
  const [visibleCommentCount, setVisibleCommentCount] = useState(5);
  const [hasMoreComments, setHasMoreComments] = useState(false);
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyContent, setReplyContent] = useState("");
  const [quotedText, setQuotedText] = useState("");
  const [quotedAuthor, setQuotedAuthor] = useState("");
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState("");
  const [commentToDelete, setCommentToDelete] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const commentsContainerRef = useRef<HTMLDivElement>(null);

  // Définir l'utilisateur actuel (simulé pour cet exemple)

  // Fonction pour récupérer les détails du sujet
  const fetchTopicDetails = async (): Promise<boolean> => {
    if (!topicId) return false;

    try {
      const token = localStorage.getItem("accessToken");
      if (!token) {
        console.error("Token d'authentification manquant");
        return false;
      }

      const response = await fetch(
        `${API_ENDPOINTS.FORUM.GET_TOPICS}/${topicId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const responseData = await response.json();
        console.log("Données du sujet récupérées:", responseData);

        // Vérifier la structure de la réponse API
        if (!responseData || !responseData.data) {
          console.error("Structure de réponse API inattendue:", responseData);
          return false;
        }

        const topicData = responseData.data;

        // Vérifier si les données du topic sont présentes
        if (!topicData || !topicData.id) {
          console.error("Données du sujet manquantes ou invalides:", topicData);
          return false;
        }

        // Créer l'objet topic avec les données de l'API
        const formattedTopic: ForumTopic = {
          id: topicData.id.toString(),
          title: topicData.title || "Sans titre",
          content: topicData.content || "",
          author: {
            name:
              topicData.author?.firstName && topicData.author?.lastName
                ? `${topicData.author.firstName} ${topicData.author.lastName}`
                : topicData.author?.name || "Utilisateur inconnu",
            avatar:
              topicData.author?.profileImage ||
              "/images/avatar-placeholder.png",
          },
          publishedAt: topicData.createdAt || new Date().toISOString(),
          subscribersCount: topicData.views || 0,
          commentsCount: 0,
          comments: [],
          category: topicData.category,
          isPinned: topicData.isPinned || false,
          isLocked: topicData.isLocked || false,
        };

        setTopic(formattedTopic);
        return true;
      } else {
        console.error(
          "Erreur lors de la récupération du sujet:",
          response.statusText
        );
        return false;
      }
    } catch (error) {
      console.error("Erreur lors de la récupération du sujet:", error);
      return false;
    }
  };

  // Fonction pour récupérer les commentaires du sujet
  const fetchTopicComments = async (): Promise<boolean> => {
    if (!topicId) return false;

    try {
      const token = localStorage.getItem("accessToken");
      if (!token) {
        console.error("Token d'authentification manquant");
        return false;
      }

      const response = await fetch(
        API_ENDPOINTS.FORUM.GET_TOPIC_COMMENTS(topicId),
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const responseData = await response.json();
        console.log("Commentaires récupérés:", responseData);

        // Vérifier la structure de la réponse API
        if (!responseData) {
          console.error("Structure de réponse API inattendue:", responseData);
          return false;
        }

        // Extraire le tableau de commentaires selon la structure de réponse
        let postsArray: PostResponse[] = [];

        if (
          responseData.success &&
          responseData.data &&
          Array.isArray(responseData.data.posts)
        ) {
          postsArray = responseData.data.posts;
        } else if (responseData.data && Array.isArray(responseData.data)) {
          postsArray = responseData.data;
        } else if (Array.isArray(responseData)) {
          postsArray = responseData;
        } else if (Array.isArray(responseData.posts)) {
          postsArray = responseData.posts;
        }

        if (postsArray.length === 0) {
          console.log("Aucun commentaire trouvé pour ce sujet");
          setTopic((prevTopic) => {
            if (!prevTopic) return null;
            return {
              ...prevTopic,
              comments: [],
              commentsCount: 0,
            };
          });
          return true;
        }

        // Formater les commentaires pour correspondre à notre interface
        const formattedComments = postsArray.map((post: PostResponse) => {
          // S'assurer que l'ID est une chaîne
          const postId =
            post && post.id ? post.id.toString() : `temp-${Date.now()}`;

          // Construire le nom de l'auteur en fonction des données disponibles
          let authorName = "Utilisateur inconnu";
          if (post && post.author) {
            if (post.author.name) {
              authorName = post.author.name;
            } else if (post.author.firstName && post.author.lastName) {
              authorName = `${post.author.firstName} ${post.author.lastName}`;
            }
          }

          // Vérifier si le commentaire a été édité
          let isEdited = false;
          if (post && post.updatedAt && post.createdAt) {
            isEdited = post.updatedAt !== post.createdAt;
          }

          return {
            id: postId,
            content: post && post.content ? post.content : "",
            author: {
              name: authorName,
              avatar:
                post && post.author && post.author.avatar
                  ? post.author.avatar
                  : "/images/avatar-placeholder.png",
            },
            publishedAt:
              post && post.createdAt
                ? post.createdAt
                : new Date().toISOString(),
            parentId:
              post && post.parentId ? post.parentId.toString() : undefined,
            isReply: !!(post && post.parentId),
            isEdited: isEdited,
            isDeleted: !!(post && post.isDeleted),
            isSolution: !!(post && post.isSolution),
            isActive: post ? post.isActive !== false : true,
            likes: post && post.likes ? post.likes : 0,
          };
        });

        // Trier les commentaires: d'abord par date (les plus anciens), puis les réponses après leurs parents
        const sortedComments = formattedComments.sort(
          (a: ForumComment, b: ForumComment) => {
            // Si l'un est une réponse à l'autre, la réponse vient après
            if (a.parentId === b.id) return 1;
            if (b.parentId === a.id) return -1;

            // Sinon, trier par date
            return (
              new Date(a.publishedAt).getTime() -
              new Date(b.publishedAt).getTime()
            );
          }
        );

        // Mettre à jour l'état avec les commentaires formatés
        setTopic((prevTopic) => {
          if (!prevTopic) return null;
          return {
            ...prevTopic,
            comments: sortedComments,
            commentsCount: sortedComments.length,
          };
        });

        return true;
      } else {
        console.error(
          "Erreur lors de la récupération des commentaires:",
          response.statusText
        );
        return false;
      }
    } catch (error) {
      console.error("Erreur lors de la récupération des commentaires:", error);
      return false;
    }
  };

  // Charger les données initiales
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);

      try {
        // D'abord charger les détails du sujet
        const topicLoaded = await fetchTopicDetails();

        // Ensuite charger les commentaires, seulement si le sujet est chargé
        if (topicLoaded) {
          await fetchTopicComments();
        }
      } catch (error) {
        console.error("Erreur lors du chargement des données:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [topicId]); // Dépendance uniquement sur topicId

  // Mettre à jour les commentaires affichés lorsque le topic change ou le nombre visible change
  useEffect(() => {
    if (topic) {
      const totalComments = topic.comments.length;
      setHasMoreComments(visibleCommentCount < totalComments);

      // Récupérer les N premiers commentaires (les plus anciens d'abord)
      const comments = topic.comments.slice(0, visibleCommentCount);
      setDisplayedComments(comments);
    }
  }, [topic, visibleCommentCount]);

  const handleLoadMoreComments = () => {
    setVisibleCommentCount((prev) =>
      Math.min(prev + 5, topic?.comments.length || 0)
    );
  };

  const handleSubmitComment = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newComment.trim() || !topicId) return;

    try {
      const token = localStorage.getItem("accessToken");
      if (!token) {
        console.error("Token d'authentification manquant");
        return;
      }

      // Préparation des données pour l'API
      const commentData = {
        content: newComment,
        topicId: topicId,
        parentId: null, // C'est un commentaire principal, pas une réponse
      };

      // Envoi du commentaire à l'API
      const response = await fetch(API_ENDPOINTS.FORUM.CREATE_COMMENT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(commentData),
      });

      if (response.ok) {
        // Format du nouveau commentaire avec les données de la réponse API
        const responseData = await response.json();
        console.log("Réponse après création de commentaire:", responseData);

        const newCommentObj: ForumComment = {
          id: responseData.id
            ? responseData.id.toString()
            : `temp-${Date.now()}`,
          content: newComment,
          author: {
            name: "Vous", // Idéalement, utiliser les données de l'utilisateur connecté
            avatar: "/images/avatar-placeholder.png",
          },
          publishedAt: responseData.createdAt || new Date().toISOString(),
          isReply: false,
        };

        if (topic) {
          const updatedComments = [...topic.comments, newCommentObj];
          const updatedTopic = {
            ...topic,
            comments: updatedComments,
            commentsCount: updatedComments.length,
          };

          setTopic(updatedTopic);
          setNewComment("");

          // Assurer que le nouveau commentaire est visible
          setVisibleCommentCount((prev) =>
            Math.max(prev, updatedComments.length)
          );

          // Faire défiler jusqu'au nouveau commentaire
          setTimeout(() => {
            if (commentsContainerRef.current) {
              commentsContainerRef.current.scrollIntoView({
                behavior: "smooth",
                block: "end",
              });
            }
          }, 100);
        }

        // Recharger les commentaires pour s'assurer que tout est synchronisé
        await fetchTopicComments();
      } else {
        console.error(
          "Erreur lors de l'envoi du commentaire:",
          response.statusText
        );
        alert(
          "Une erreur est survenue lors de l'envoi de votre commentaire. Veuillez réessayer."
        );
      }
    } catch (error) {
      console.error("Erreur lors de l'envoi du commentaire:", error);
      alert(
        "Une erreur est survenue lors de l'envoi de votre commentaire. Veuillez réessayer."
      );
    }
  };

  const handleReplyClick = (commentId: string) => {
    // Trouver le commentaire auquel on répond
    const commentToReply = topic?.comments.find((c) => c.id === commentId);
    if (!commentToReply) return;

    // Obtenir un extrait du contenu pour citer (limité à 100 caractères pour éviter des citations trop longues)
    const quoteText =
      commentToReply.content.length > 100
        ? `${commentToReply.content.substring(0, 100)}...`
        : commentToReply.content;

    setReplyingTo(commentId);
    setReplyContent("");
    setQuotedText(quoteText);
    setQuotedAuthor(commentToReply.author.name);

    // Faire défiler jusqu'au formulaire de réponse après un court délai
    setTimeout(() => {
      const replyForm = document.getElementById(`reply-form-${commentId}`);
      if (replyForm) {
        replyForm.scrollIntoView({ behavior: "smooth" });
        const textarea = replyForm.querySelector("textarea");
        if (textarea) {
          textarea.focus();
        }
      }
    }, 100);
  };

  const handleCancelReply = () => {
    setReplyingTo(null);
    setReplyContent("");
    setQuotedText("");
    setQuotedAuthor("");
  };

  const handleSubmitReply = async (commentId: string) => {
    if (!replyContent.trim() || !topicId) return;

    try {
      const token = localStorage.getItem("accessToken");
      if (!token) {
        console.error("Token d'authentification manquant");
        return;
      }

      // Trouver le commentaire auquel on répond
      const parentComment = topic?.comments.find((c) => c.id === commentId);
      if (!parentComment || !topic) return;

      // Préparation des données pour l'API
      const replyData = {
        content: replyContent,
        topicId: topicId,
        parentId: commentId,
        quotedText: quotedText,
        quotedAuthor: quotedAuthor,
      };

      // Envoi de la réponse à l'API
      const response = await fetch(API_ENDPOINTS.FORUM.CREATE_COMMENT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(replyData),
      });

      if (response.ok) {
        // Extraire les données de la réponse
        const responseData = await response.json();
        console.log("Réponse après création de réponse:", responseData);

        // Format de la nouvelle réponse avec les données de la réponse API
        const replyComment: ForumComment = {
          id: responseData.id
            ? responseData.id.toString()
            : `reply-${Date.now()}`,
          content: replyContent,
          author: {
            name: "Vous", // Idéalement, utiliser les données de l'utilisateur connecté
            avatar: "/images/avatar-placeholder.png",
          },
          publishedAt: responseData.createdAt || new Date().toISOString(),
          parentId: commentId,
          isReply: true,
          quotedText: quotedText,
          quotedAuthor: quotedAuthor,
        };

        // Ajouter la réponse à la liste des commentaires
        const updatedComments = [...topic.comments, replyComment];
        const updatedTopic = {
          ...topic,
          comments: updatedComments,
          commentsCount: updatedComments.length,
        };

        setTopic(updatedTopic);
        setReplyContent("");
        setReplyingTo(null);
        setQuotedText("");
        setQuotedAuthor("");

        // Assurer que la nouvelle réponse est visible
        setVisibleCommentCount((prev) =>
          Math.max(prev, updatedComments.length)
        );

        // Faire défiler jusqu'à la nouvelle réponse
        setTimeout(() => {
          const newReply = document.getElementById(
            `comment-${replyComment.id}`
          );
          if (newReply) {
            newReply.scrollIntoView({ behavior: "smooth" });
          }
        }, 100);

        // Recharger les commentaires pour s'assurer que tout est synchronisé
        await fetchTopicComments();
      } else {
        console.error(
          "Erreur lors de l'envoi de la réponse:",
          response.statusText
        );
        alert(
          "Une erreur est survenue lors de l'envoi de votre réponse. Veuillez réessayer."
        );
      }
    } catch (error) {
      console.error("Erreur lors de l'envoi de la réponse:", error);
      alert(
        "Une erreur est survenue lors de l'envoi de votre réponse. Veuillez réessayer."
      );
    }
  };

  const handleGoBack = () => {
    navigate("/etudiant/forum");
  };

  const formatDate = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = {
      year: "numeric",
      month: "long",
      day: "numeric",
    };
    return new Date(dateString).toLocaleDateString("fr-FR", options);
  };

  // Gestion de l'édition d'un commentaire
  const handleEditClick = (commentId: string) => {
    // Vérifier que le commentaire existe
    const commentToEdit = topic?.comments.find((c) => c.id === commentId);
    if (!commentToEdit) return;

    // Activer le mode édition et initialiser le contenu d'édition
    setEditingCommentId(commentId);
    setEditContent(commentToEdit.content);

    // Fermer tout formulaire de réponse ouvert
    if (replyingTo) {
      setReplyingTo(null);
      setReplyContent("");
      setQuotedText("");
      setQuotedAuthor("");
    }

    // Faire défiler jusqu'au formulaire d'édition après un court délai
    setTimeout(() => {
      const editForm = document.getElementById(`edit-form-${commentId}`);
      if (editForm) {
        editForm.scrollIntoView({ behavior: "smooth" });
        const textarea = editForm.querySelector("textarea");
        if (textarea) {
          textarea.focus();
        }
      }
    }, 100);
  };

  const handleCancelEdit = () => {
    setEditingCommentId(null);
    setEditContent("");
  };

  const handleSaveEdit = async (commentId: string) => {
    if (!editContent.trim() || !topic || !topicId) return;

    try {
      const token = localStorage.getItem("accessToken");
      if (!token) {
        console.error("Token d'authentification manquant");
        return;
      }

      // Préparer les données pour l'API
      const editData = {
        content: editContent,
      };

      // Envoyer la modification à l'API
      const response = await fetch(
        API_ENDPOINTS.FORUM.UPDATE_COMMENT(commentId),
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(editData),
        }
      );

      if (response.ok) {
        const responseData = await response.json();
        console.log("Réponse après modification:", responseData);

        // Mise à jour locale
        const updatedComments = topic.comments.map((comment) =>
          comment.id === commentId
            ? {
                ...comment,
                content: editContent,
                isEdited: true,
              }
            : comment
        );

        const updatedTopic = {
          ...topic,
          comments: updatedComments,
        };

        setTopic(updatedTopic);
        setEditingCommentId(null);
        setEditContent("");

        // Faire défiler jusqu'au commentaire modifié
        setTimeout(() => {
          const editedComment = document.getElementById(`comment-${commentId}`);
          if (editedComment) {
            editedComment.scrollIntoView({ behavior: "smooth" });
            // Ajouter une classe pour mettre en évidence la modification pendant un court instant
            editedComment.classList.add("bg-yellow-50");
            setTimeout(() => {
              editedComment.classList.remove("bg-yellow-50");
            }, 1500);
          }
        }, 100);

        // Recharger les commentaires pour s'assurer que tout est synchronisé
        await fetchTopicComments();
      } else {
        console.error(
          "Erreur lors de la modification du commentaire:",
          response.statusText
        );
        alert(
          "Une erreur est survenue lors de la modification de votre commentaire. Veuillez réessayer."
        );
      }
    } catch (error) {
      console.error("Erreur lors de la modification du commentaire:", error);
      alert(
        "Une erreur est survenue lors de la modification de votre commentaire. Veuillez réessayer."
      );
    }
  };

  // Gestion de la suppression d'un commentaire
  const handleDeleteClick = (commentId: string) => {
    // Vérifier que le commentaire existe
    const commentToDelete = topic?.comments.find((c) => c.id === commentId);
    if (!commentToDelete) return;

    // Afficher la confirmation de suppression
    setCommentToDelete(commentId);
    setShowDeleteConfirm(true);

    // Fermer tout formulaire d'édition ou de réponse ouvert
    if (editingCommentId) {
      setEditingCommentId(null);
      setEditContent("");
    }

    if (replyingTo) {
      setReplyingTo(null);
      setReplyContent("");
      setQuotedText("");
      setQuotedAuthor("");
    }
  };

  const handleCancelDelete = () => {
    setCommentToDelete(null);
    setShowDeleteConfirm(false);
  };

  const handleConfirmDelete = async () => {
    if (!commentToDelete || !topic || !topicId) return;

    try {
      const token = localStorage.getItem("accessToken");
      if (!token) {
        console.error("Token d'authentification manquant");
        return;
      }

      // Envoyer la demande de suppression à l'API
      const response = await fetch(
        API_ENDPOINTS.FORUM.DELETE_COMMENT(topicId, commentToDelete),
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        // Trouver toutes les réponses au commentaire à supprimer
        const commentReplies = topic.comments.filter(
          (c) => c.parentId === commentToDelete
        );

        // Supprimer le commentaire et ses réponses
        let updatedComments;
        if (commentReplies.length > 0) {
          // Si le commentaire a des réponses, le marquer comme supprimé au lieu de le supprimer complètement
          updatedComments = topic.comments.map((comment) =>
            comment.id === commentToDelete
              ? {
                  ...comment,
                  content: "[Ce message a été supprimé]",
                  isDeleted: true,
                  author: {
                    name: comment.author.name,
                    avatar: comment.author.avatar,
                  },
                }
              : comment
          );
        } else {
          // Si le commentaire n'a pas de réponses, le supprimer complètement
          updatedComments = topic.comments.filter(
            (comment) => comment.id !== commentToDelete
          );
        }

        const updatedTopic = {
          ...topic,
          comments: updatedComments,
          commentsCount: updatedComments.length,
        };

        setTopic(updatedTopic);
        setCommentToDelete(null);
        setShowDeleteConfirm(false);

        // Mettre à jour l'affichage des commentaires
        if (updatedComments.length < visibleCommentCount) {
          setVisibleCommentCount(updatedComments.length);
        }

        // Recharger les commentaires pour s'assurer que tout est synchronisé
        await fetchTopicComments();
      } else {
        console.error(
          "Erreur lors de la suppression du commentaire:",
          response.statusText
        );
        alert(
          "Une erreur est survenue lors de la suppression de votre commentaire. Veuillez réessayer."
        );
      }
    } catch (error) {
      console.error("Erreur lors de la suppression du commentaire:", error);
      alert(
        "Une erreur est survenue lors de la suppression de votre commentaire. Veuillez réessayer."
      );
    }
  };

  if (isLoading) {
    return (
      <div className="bg-[#fefaf6]">
        <div className="container mx-auto py-2 px-4">
          <div className="flex justify-center items-center h-64">
            <p className="text-xl text-gray-600">Chargement du sujet...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!topic) {
    return (
      <div className="bg-[#fefaf6]">
        <div className="container mx-auto py-2 px-4">
          <div className="flex flex-col items-center py-10">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              Sujet non trouvé
            </h2>
            <p className="text-gray-600 mb-6">
              Le sujet que vous recherchez n'existe pas ou a été supprimé.
            </p>
            <button
              onClick={handleGoBack}
              className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-md"
            >
              <FiArrowLeft /> Retourner au forum
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#fefaf6]">
      <div className="container mx-auto py-2 px-4">
        <div className="flex flex-col w-full">
          {/* Bouton de retour et titre */}
          <div className="mb-6">
            <button
              onClick={handleGoBack}
              className="flex items-center gap-2 text-orange-500 hover:text-orange-600 mb-4"
            >
              <FiArrowLeft /> Retour au forum
            </button>
            <h1 className="text-2xl font-bold text-gray-800">{topic.title}</h1>
            {topic.category && (
              <div className="mt-1">
                <span className="inline-block bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">
                  {topic.category.name}
                </span>
                {topic.isPinned && (
                  <span className="inline-block bg-orange-100 text-orange-800 text-xs px-2 py-1 rounded-full ml-2">
                    Épinglé
                  </span>
                )}
                {topic.isLocked && (
                  <span className="inline-block bg-gray-100 text-gray-800 text-xs px-2 py-1 rounded-full ml-2">
                    Verrouillé
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Contenu du sujet */}
          <div className="bg-white p-6 rounded-lg shadow-sm mb-8">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-10 h-10 rounded-full bg-green-100 flex-shrink-0 flex items-center justify-center text-orange-500">
                <FiUser size={20} />
              </div>
              <div>
                <div className="font-medium">{topic.author.name}</div>
                <div className="text-sm text-gray-500">
                  Publié le {formatDate(topic.publishedAt)}
                </div>
              </div>
            </div>
            <p className="text-gray-700 whitespace-pre-line">{topic.content}</p>
          </div>

          {/* Section des commentaires */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">
              Commentaires ({topic.comments.length})
            </h2>

            {topic.comments.length === 0 ? (
              <div className="bg-white p-6 rounded-lg text-center">
                <p className="text-gray-600">
                  Aucun commentaire pour le moment. Soyez le premier à réagir!
                </p>
              </div>
            ) : (
              <div ref={commentsContainerRef}>
                {/* Bouton pour charger plus de commentaires */}
                {hasMoreComments && (
                  <button
                    onClick={handleLoadMoreComments}
                    className="flex items-center justify-center w-full py-2 bg-gray-100 text-gray-700 mb-4 rounded-md hover:bg-gray-200 transition-colors"
                  >
                    <FiChevronUp className="mr-2" /> Afficher plus de
                    commentaires
                  </button>
                )}

                {/* Liste des commentaires */}
                {displayedComments.map((comment) => (
                  <div
                    key={comment.id}
                    id={`comment-${comment.id}`}
                    className={`bg-white p-4 rounded-lg mb-4 ${
                      comment.isReply
                        ? "ml-10 border-l-2 border-orange-200"
                        : ""
                    } ${comment.isDeleted ? "opacity-70" : ""}`}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full bg-green-100 flex-shrink-0 flex items-center justify-center text-orange-500">
                          <FiUser size={18} />
                        </div>
                        <div>
                          <div className="font-medium flex items-center">
                            {comment.author.name}
                            {comment.isEdited && (
                              <span className="text-xs text-gray-500 ml-2">
                                (modifié)
                              </span>
                            )}
                            {comment.isSolution && (
                              <span className="ml-2 bg-green-100 text-green-800 text-xs px-2 py-0.5 rounded-full">
                                Solution
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-gray-500">
                            {formatDate(comment.publishedAt)}
                          </div>
                        </div>
                      </div>

                      {/* Actions sur les commentaires */}
                      {!comment.isDeleted && (
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleReplyClick(comment.id)}
                            className="text-sm text-gray-500 hover:text-orange-500"
                            disabled={!!replyingTo || !!editingCommentId}
                          >
                            Répondre
                          </button>
                          {comment.author.name === "Vous" && (
                            <>
                              <button
                                onClick={() => handleEditClick(comment.id)}
                                className="text-sm text-gray-500 hover:text-blue-500"
                                disabled={!!replyingTo || !!editingCommentId}
                              >
                                <FiEdit2 size={16} />
                              </button>
                              <button
                                onClick={() => handleDeleteClick(comment.id)}
                                className="text-sm text-gray-500 hover:text-red-500"
                                disabled={!!replyingTo || !!editingCommentId}
                              >
                                <FiTrash2 size={16} />
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Texte cité pour les réponses */}
                    {comment.isReply && comment.quotedText && (
                      <div className="bg-gray-50 p-2 rounded my-2 text-sm text-gray-600 border-l-2 border-gray-300">
                        <p className="font-medium text-xs text-gray-500">
                          {comment.quotedAuthor} a écrit :
                        </p>
                        <p>{comment.quotedText}</p>
                      </div>
                    )}

                    {/* Contenu du commentaire */}
                    {editingCommentId === comment.id ? (
                      <div id={`edit-form-${comment.id}`} className="mt-3">
                        <textarea
                          value={editContent}
                          onChange={(e) => setEditContent(e.target.value)}
                          className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                          rows={4}
                          placeholder="Modifier votre commentaire..."
                        />
                        <div className="flex space-x-2 mt-2">
                          <button
                            onClick={() => handleSaveEdit(comment.id)}
                            className="bg-green-500 text-white px-3 py-1 rounded-md hover:bg-green-600 flex items-center"
                          >
                            <FiCheck className="mr-1" /> Enregistrer
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            className="bg-gray-300 text-gray-700 px-3 py-1 rounded-md hover:bg-gray-400 flex items-center"
                          >
                            <FiX className="mr-1" /> Annuler
                          </button>
                        </div>
                      </div>
                    ) : (
                      <p className="mt-3 text-gray-700 whitespace-pre-line">
                        {comment.content}
                      </p>
                    )}

                    {/* Formulaire de réponse */}
                    {replyingTo === comment.id && (
                      <div
                        id={`reply-form-${comment.id}`}
                        className="mt-4 bg-gray-50 p-3 rounded-md"
                      >
                        <div className="flex items-center text-sm text-gray-600 mb-2">
                          <FiCornerDownRight className="mr-2" /> Répondre à{" "}
                          {comment.author.name}
                        </div>
                        <textarea
                          value={replyContent}
                          onChange={(e) => setReplyContent(e.target.value)}
                          className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                          rows={3}
                          placeholder="Écrivez votre réponse..."
                        />
                        <div className="flex space-x-2 mt-2">
                          <button
                            onClick={() => handleSubmitReply(comment.id)}
                            className="bg-orange-500 text-white px-3 py-1 rounded-md hover:bg-orange-600 flex items-center"
                          >
                            <FiSend className="mr-1" /> Envoyer
                          </button>
                          <button
                            onClick={handleCancelReply}
                            className="bg-gray-300 text-gray-700 px-3 py-1 rounded-md hover:bg-gray-400 flex items-center"
                          >
                            <FiX className="mr-1" /> Annuler
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Formulaire pour ajouter un commentaire */}
          {!topic.isLocked && (
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                Ajouter un commentaire
              </h3>
              <form onSubmit={handleSubmitComment}>
                <textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  rows={5}
                  placeholder="Partagez votre avis..."
                />
                <div className="flex justify-end mt-3">
                  <button
                    type="submit"
                    disabled={!newComment.trim()}
                    className={`px-4 py-2 rounded-md flex items-center ${
                      newComment.trim()
                        ? "bg-orange-500 hover:bg-orange-600 text-white"
                        : "bg-gray-200 text-gray-400 cursor-not-allowed"
                    }`}
                  >
                    <FiSend className="mr-2" /> Publier
                  </button>
                </div>
              </form>
            </div>
          )}

          {topic.isLocked && (
            <div className="bg-gray-100 p-4 rounded-lg text-center">
              <div className="flex items-center justify-center text-gray-600">
                <FiAlertTriangle className="mr-2 text-orange-500" /> Ce sujet
                est verrouillé. Vous ne pouvez plus y répondre.
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal de confirmation de suppression */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md w-full">
            <h3 className="text-xl font-bold text-gray-800 mb-4">
              Confirmer la suppression
            </h3>
            <p className="text-gray-600 mb-6">
              Êtes-vous sûr de vouloir supprimer ce commentaire ? Cette action
              ne peut pas être annulée.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={handleCancelDelete}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
              >
                Annuler
              </button>
              <button
                onClick={handleConfirmDelete}
                className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600"
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ForumTopicDetail;
